import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiHelper, isApiHelperErrorResponse } from '../ApiHelper';
import { DetailsList, DetailsListLayoutMode, IColumn } from '@fluentui/react/lib/DetailsList';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { DefaultButton, PrimaryButton, IconButton } from '@fluentui/react';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { TextField } from '@fluentui/react/lib/TextField';
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { useBoolean } from '@fluentui/react-hooks';
import { Separator } from '@fluentui/react/lib/Separator';
import { DatePicker } from '@fluentui/react/lib/DatePicker';
import '../styles/common.css';
import { formatLocalDateOnly, getLocalTodayDateOnly, normalizeApiDateOnly, parseLocalDateOnly } from '../utils/localDateOnly';

interface AdhocApiProfileResponse {
    id: string;
    userAlias: string;
    createdDate: string;
    date: string;
    farm: string;
    api: string;
    maxRequestCount: number;
    successOnly: boolean;
    kqlFilter?: string;
    status: number; // 0=Created, 1=InProgress, 2=Succeeded, 3=Failed
    progress: number;
}

interface AdhocApiProfileListResponse {
    profiles: AdhocApiProfileResponse[];
    totalCount: number;
}

interface CreateAdhocApiProfileRequest {
    date: string;
    farm: string;
    api: string;
    maxRequestCount: number;
    successOnly: boolean;
    kqlFilter?: string;
}

const StatusNames = {
    0: 'Created',
    1: 'In Progress',
    2: 'Succeeded',
    3: 'Failed',
    4: 'Farm Not Supported',
    5: 'Bad Query',
    6: 'Too Few Requests',
    7: 'No Profiled Requests',
    8: 'Partial Success',
    9: 'No Machine Profiles'
};

const StatusColors = {
    0: '#605e5c', // Gray - Created
    1: '#0078d4', // Blue - In Progress
    2: '#107c10', // Green - Succeeded
    3: '#d83b01', // Red - Failed
    4: '#d83b01', // Red - Farm Not Supported
    5: '#d83b01', // Red - Bad Query
    6: '#e97548', // Orange - Too Few Requests
    7: '#d83b01', // Red - No Profiled Requests
    8: '#ca5010', // Orange - Partial Success
    9: '#e97548'  // Orange - No Machine Profiles
};

export function AdhocApiProfiles() {
    const navigate = useNavigate();
    window.document.title = 'PR Guardian - Adhoc api profiler';

    const [profiles, setProfiles] = useState<AdhocApiProfileResponse[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [mineOnly, setMineOnly] = useState<boolean>(true);
    const [pageSize] = useState<number>(30);
    const [pageNumber, setPageNumber] = useState<number>(0);

    // Panel for create form
    const [isCreatePanelOpen, { setTrue: openCreatePanel, setFalse: closeCreatePanel }] = useBoolean(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState<CreateAdhocApiProfileRequest>({
        date: getLocalTodayDateOnly(),
        farm: '',
        api: '',
        maxRequestCount: 100,
        successOnly: true,
        kqlFilter: ''
    });

    const fetchProfiles = async (selfOnly: boolean, page: number = 0) => {
        try {
            setError(null);
            setLoading(true);

            const url = `/api/v1/adhoc-api-profiles?selfOnly=${selfOnly}&pageSize=${pageSize}&pageNumber=${page}`;
            const response = await ApiHelper<AdhocApiProfileListResponse>(url, 'GET');
            setProfiles(response.profiles || []);
            setTotalCount(response.totalCount || 0);
        } catch (err) {
            console.error('Error fetching profiles:', err);
            setError('An error occurred while loading profiles');
            setProfiles([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const createProfile = async () => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);

            // Validate form
            if (!formData.farm || !formData.api) {
                setSubmitError('Farm and API are required');
                return;
            }

            const requestBody = {
                ...formData,
                kqlFilter: formData.kqlFilter || undefined
            };

            const response = await ApiHelper<AdhocApiProfileResponse, { message?: string }>('/api/v1/adhoc-api-profiles', 'POST', requestBody, true);

            if (isApiHelperErrorResponse<{ message?: string }>(response)) {
                const serverMessage = response.data?.message;
                setSubmitError(serverMessage || `Failed to create profile (status ${response.status})`);
                return;
            }

            if (response.id) {
                // Success - close panel and navigate to the new profile
                closeCreatePanel();
                resetForm();
                navigate(`/adhoc-profiles/${response.id}`);
            } else {
                setSubmitError('Failed to create profile');
            }
        } catch (err) {
            console.error('Error creating profile:', err);
            setSubmitError('An error occurred while creating the profile');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            date: getLocalTodayDateOnly(),
            farm: '',
            api: '',
            maxRequestCount: 100,
            successOnly: true,
            kqlFilter: ''
        });
        setSubmitError(null);
    };

    useEffect(() => {
        fetchProfiles(mineOnly, pageNumber);
    }, [mineOnly, pageNumber]);

    const commandItems: ICommandBarItemProps[] = [
        {
            key: 'create',
            text: 'Create New Profile',
            iconProps: { iconName: 'Add' },
            onClick: openCreatePanel
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => fetchProfiles(mineOnly, pageNumber)
        }
    ];

    const columns: IColumn[] = [
        {
            key: 'api',
            name: 'API',
            fieldName: 'api',
            minWidth: 200,
            maxWidth: 400,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate(`/adhoc-profiles/${item.id}`);
                    }}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                >
                    {item.api}
                </a>
            )
        },
        {
            key: 'farm',
            name: 'Farm label',
            fieldName: 'farm',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true
        },
        {
            key: 'date',
            name: 'Target Date',
            fieldName: 'date',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) =>
                normalizeApiDateOnly(item.date)
        },
        {
            key: 'status',
            name: 'Status',
            fieldName: 'status',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) => (
                <span style={{ color: StatusColors[item.status] }}>
                    {StatusNames[item.status]}
                </span>
            )
        },
        {
            key: 'userAlias',
            name: 'Created By',
            fieldName: 'userAlias',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true
        },
        {
            key: 'maxRequestCount',
            name: 'Max Requests',
            fieldName: 'maxRequestCount',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) => item.maxRequestCount.toLocaleString()
        },
        {
            key: 'successOnly',
            name: 'Success Only',
            fieldName: 'successOnly',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) => item.successOnly ? 'Yes' : 'No'
        },
        {
            key: 'createdDate',
            name: 'Created',
            fieldName: 'createdDate',
            minWidth: 130,
            maxWidth: 160,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) =>
                new Date(item.createdDate).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
        },
        {
            key: 'kqlFilter',
            name: 'KQL filter',
            fieldName: 'kqlFilter',
            minWidth: 130,
            maxWidth: 160,
            isResizable: true,
            onRender: (item: AdhocApiProfileResponse) => {
                if (item.kqlFilter)
                    return item.kqlFilter.substring(0, 30) + '...';
                return '';
                }
        },
        {
            key: 'actions',
            name: 'Actions',
            fieldName: 'actions',
            minWidth: 60,
            maxWidth: 80,
            isResizable: false,
            onRender: (item: AdhocApiProfileResponse) => (
                <TooltipHost content="Create similar profile">
                    <IconButton
                        iconProps={{ iconName: 'Copy' }}
                        title="Create similar profile"
                        ariaLabel="Create similar profile"
                        onClick={(e) => {
                            e.stopPropagation();
                            setFormData({
                                date: normalizeApiDateOnly(item.date),
                                farm: item.farm,
                                api: item.api,
                                maxRequestCount: item.maxRequestCount,
                                successOnly: item.successOnly,
                                kqlFilter: item.kqlFilter || ''
                            });
                            openCreatePanel();
                        }}
                        styles={{
                            root: { color: '#0078d4' }
                        }}
                    />
                </TooltipHost>
            )
        }
    ];

    return (
        <div className="page-container">
            <Stack tokens={{ childrenGap: 20 }}>
                <CommandBar items={commandItems} />

                {/* Filter Controls */}
                <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
                    <Checkbox
                        label="Mine only"
                        checked={mineOnly}
                        onChange={(ev, checked) => {
                            setMineOnly(!!checked);
                            setPageNumber(0);
                        }}
                    />
                    <Text variant="medium" style={{ color: '#605e5c' }}>
                        {loading ? 'Loading...' : `${totalCount} total profiles`}
                    </Text>
                </Stack>

                {error && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {error}
                    </MessageBar>
                )}

                {loading ? (
                    <Spinner size={SpinnerSize.large} label="Loading profiles..." />
                ) : (
                    <DetailsList
                        items={profiles}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={0}
                        compact={true}
                        onItemInvoked={(item: AdhocApiProfileResponse) => {
                            // Navigate to profile detail page
                            navigate(`/adhoc-profiles/${item.id}`);
                        }}
                    />
                )}

                {/* Pagination info */}
                {!loading && profiles.length > 0 && (
                    <Text variant="medium" style={{ color: '#605e5c' }}>
                        Showing {profiles.length} profiles (Page {pageNumber + 1})
                    </Text>
                )}
            </Stack>

            {/* Create Profile Panel */}
            <Panel
                isOpen={isCreatePanelOpen}
                onDismiss={closeCreatePanel}
                type={PanelType.medium}
                headerText="Create New Adhoc API Profile"
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton
                            text="Create Profile"
                            onClick={createProfile}
                            disabled={isSubmitting}
                        />
                        <DefaultButton
                            text="Cancel"
                            onClick={() => {
                                closeCreatePanel();
                                resetForm();
                            }}
                            disabled={isSubmitting}
                        />
                    </Stack>
                )}
            >
                <Stack tokens={{ childrenGap: 15 }}>
                    {submitError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {submitError}
                        </MessageBar>
                    )}

                    {isSubmitting && (
                        <Spinner size={SpinnerSize.medium} label="Creating profile..." />
                    )}

                    <DatePicker
                        label="Date"
                        value={parseLocalDateOnly(formData.date)}
                        onSelectDate={(date) => {
                            if (date) {
                                setFormData(prev => ({
                                    ...prev,
                                    date: formatLocalDateOnly(date)
                                }));
                            }
                        }}
                        isRequired
                        disabled={isSubmitting}
                    />

                    <TextField
                        label="API"
                        value={formData.api}
                        onChange={(ev, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                api: newValue || ''
                            }));
                        }}
                        placeholder="e.g., SPRequest_Vroom21_ItemGet"
                        required
                        disabled={isSubmitting}
                    />

                    <TextField
                        label="Farm label (must log into eastus2 kusto)"
                        value={formData.farm}
                        onChange={(ev, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                farm: newValue || ''
                            }));
                        }}
                        placeholder="e.g., MSIT_US_1_CONTENT"
                        required
                        disabled={isSubmitting}
                    />

                    <TextField
                        label="Max Request Count"
                        type="number"
                        value={formData.maxRequestCount.toString()}
                        onChange={(ev, newValue) => {
                            const num = parseInt(newValue || '100', 10);
                            setFormData(prev => ({
                                ...prev,
                                maxRequestCount: isNaN(num) ? 100 : Math.max(1, num)
                            }));
                        }}
                        min={1}
                        disabled={isSubmitting}
                    />

                    <Checkbox
                        label="Successful requests only "
                        checked={formData.successOnly}
                        onChange={(ev, checked) => {
                            setFormData(prev => ({
                                ...prev,
                                successOnly: !!checked
                            }));
                        }}
                        disabled={isSubmitting}
                    />

                    <TextField
                        label="Extra KQL Filter (optional)"
                        value={formData.kqlFilter}
                        onChange={(ev, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                kqlFilter: newValue || ''
                            }));
                        }}
                        placeholder="e.g. | where tenantType == 'Consumer'"
                        multiline
                        rows={3}
                        disabled={isSubmitting}
                    />

                    <Separator />

                    <Text variant="small" style={{ color: '#605e5c' }}>
                        * Required fields
                    </Text>
                </Stack>
            </Panel>
        </div>
    );
}